import { parentNodeTypes } from '@constants/canvas/general';
import { matchesModelFiltersDto } from '@context/ProjectFilters/matchesModelFilters';
import { questionnaireViewFilters } from '@context/ProjectFilters/questionnaireViewFilters';
import { useToast } from '@context/Toast/ToastProvider';
import { NodeDTO } from '@store/services/nodes/types';
import { useModelFilters } from '@store/zustand/model-filters';
import { useQuestionnaireResult } from '@store/zustand/questionnaire-result';

/**
 * After a save, warns when the active model filters hide the entity that was
 * just written, and reports whether they do so that the caller can drop its
 * own success toast rather than stack the two.
 *
 * Answers from the saved DTO the caller already holds rather than a request,
 * because it runs in the save handler where no round-trip is available. It
 * goes through the shared predicate, so every category the canvas filters by
 * is covered and the warning agrees with what the canvas shows, including the
 * per-entity selection the questionnaire-result view judges by. The sub-layer
 * the predicate's layer rules key on is derived from the DTO's type. The
 * view's cascades and its report-inclusion rules need the whole graph and are
 * not evaluated here.
 */
const useEntityVisibilityCheck = () => {
  const { showToast } = useToast();
  const filters = useModelFilters();
  const { result: questionnaireResult } = useQuestionnaireResult();

  const checkIsEntityBecameHidden = (node: NodeDTO) => {
    const subLayer = parentNodeTypes[node.type as keyof typeof parentNodeTypes];

    const isMatch = matchesModelFiltersDto(
      node,
      subLayer,
      questionnaireViewFilters(
        filters,
        node,
        subLayer,
        Boolean(questionnaireResult),
      ),
    );

    if (isMatch) return false;

    showToast(
      'To make it visible please select the appropriate filtering options.',
      'success',
      {
        header: 'Entity saved successfully, but not visible.',
        width: '540px !important',
      },
    );

    return true;
  };

  return { checkIsEntityBecameHidden };
};

export default useEntityVisibilityCheck;
